(() => {
  "use strict";
  if (window.kindriHome) { window.kindriHome.init(document); return; }
  const instances = new Map();
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  const setup = root => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const cleanups = [];
    const on = (target, event, callback, options = {}) => {
      if (target) target.addEventListener(event, callback, { ...options, signal });
    };
    const one = selector => root.querySelector(selector);
    const all = selector => [...root.querySelectorAll(selector)];
    root.classList.add("kr-enhanced");
    const anchor = root.dataset.krAnchor;
    if (anchor && !document.getElementById(anchor)) root.id = anchor;
    if (root.dataset.krSection === "header") {
      const toggle = one("[data-kr-menu-toggle]");
      const nav = one("[data-kr-nav]");
      const dialog = one("[data-kr-search-dialog]");
      const search = one("[data-kr-search-toggle]");
      const setMenu = open => {
        toggle?.setAttribute("aria-expanded", String(open));
        toggle?.setAttribute("aria-label", open ? toggle.dataset.krCloseLabel : toggle.dataset.krOpenLabel);
        nav?.classList.toggle("kr-is-open", open);
      };
      on(toggle, "click", () => setMenu(toggle.getAttribute("aria-expanded") !== "true"));
      all("[data-kr-nav] a").forEach(a => on(a, "click", () => setMenu(false)));
      on(document, "click", e => { if (!root.contains(e.target)) setMenu(false); });
      on(document, "keydown", e => {
        if (e.key === "Escape" && toggle?.getAttribute("aria-expanded") === "true") {
          setMenu(false); toggle.focus();
        }
      });
      on(matchMedia("(min-width: 801px)"), "change", () => setMenu(false));
      on(search, "click", () => {
        setMenu(false);
        if (typeof dialog?.showModal !== "function") {
          location.assign(root.querySelector('form[role="search"]').action); return;
        }
        dialog.showModal();
        document.body.classList.add("kr-modal-open");
        one("[data-kr-search-input]")?.focus();
      });
      on(one("[data-kr-search-close]"), "click", () => dialog.close());
      on(dialog, "close", () => { document.body.classList.remove("kr-modal-open"); search?.focus(); });
      on(dialog, "click", e => {
        const rect = dialog.getBoundingClientRect();
        if (e.target === dialog && (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom)) dialog.close();
      });
      const updateCount = count => {
        if (!Number.isInteger(count)) return;
        all("[data-kr-cart-count]").forEach(el => { el.textContent = count; });
        all("[data-kr-cart-link]").forEach(el => el.setAttribute("aria-label", `${el.dataset.krCartLabel} (${count})`));
      };
      const refreshCart = () => {
        const cartUrl = one("[data-kr-cart-link]")?.getAttribute("href");
        if (!cartUrl) return;
        fetch(`${cartUrl}.js`, { headers: { Accept: "application/json" }, signal })
          .then(r => r.ok ? r.json() : null).then(data => { if (data) updateCount(data.item_count); }).catch(() => {});
      };
      on(window, "pageshow", event => { if (event.persisted) refreshCart(); });
      on(document, "cart:updated", e => e.detail?.item_count !== undefined ? updateCount(e.detail.item_count) : refreshCart());
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        cleanups.push(subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCart));
      }
      cleanups.push(() => { if (dialog?.open) dialog.close(); document.body.classList.remove("kr-modal-open"); });
    }
    if (root.dataset.krSection === "hero") {
      const video = one("[data-kr-hero-video]");
      const media = one("[data-kr-hero-media]");
      const toggle = one("[data-kr-video-toggle]");
      if (video && toggle) {
        let failed = false;
        const sync = () => {
          const playing = !video.paused && !video.ended;
          toggle.setAttribute("aria-label", playing ? toggle.dataset.krPauseLabel : toggle.dataset.krPlayLabel);
          one("[data-kr-video-text]").textContent = playing ? toggle.dataset.krPauseText : toggle.dataset.krPlayText;
          one("[data-kr-video-icon]").textContent = playing ? "Ⅱ" : "▷";
        };
        const play = async () => {
          if (failed) return;
          if (!video.getAttribute("src")) video.src = video.dataset.krVideoSrc;
          video.muted = true;
          try { await video.play(); } catch { /* Poster and explicit play remain available. */ }
          if (!signal.aborted) sync();
        };
        on(video, "playing", () => { media.classList.add("kr-has-video"); sync(); });
        on(video, "pause", sync);
        on(video, "error", () => { failed = true; media.classList.remove("kr-has-video"); toggle.hidden = true; });
        on(toggle, "click", () => video.paused ? play() : video.pause());
        on(motion, "change", () => { if (motion.matches) { video.autoplay = false; video.pause(); } });
        toggle.hidden = false;
        if (!motion.matches && video.dataset.krAutoplay === "true") { video.autoplay = true; play(); }
        cleanups.push(() => { video.pause(); video.removeAttribute("src"); video.load(); });
      }
    }
    if (root.dataset.krSection === "ritual") {
      const tablist = one("[data-kr-ritual-tabs]");
      if (tablist) tablist.hidden = false;
      const tabs = all('[role="tab"]');
      const select = (tab, focus = false) => {
        tabs.forEach(item => {
          const active = item === tab;
          item.setAttribute("aria-selected", String(active));
          item.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(item.getAttribute("aria-controls"));
          if (panel && root.contains(panel)) { panel.hidden = !active; panel.classList.toggle("kr-entering", active); }
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        on(tab, "click", () => select(tab));
        on(tab, "keydown", e => {
          let next = index;
          if (["ArrowRight", "ArrowDown"].includes(e.key)) next = (index + 1) % tabs.length;
          else if (["ArrowLeft", "ArrowUp"].includes(e.key)) next = (index + tabs.length - 1) % tabs.length;
          else if (e.key === "Home") next = 0;
          else if (e.key === "End") next = tabs.length - 1;
          else return;
          e.preventDefault(); select(tabs[next], true);
        });
      });
      if (tabs.length) select(tabs[0]);
      cleanups.push(() => {
        all('[role="tabpanel"]').forEach(panel => { panel.hidden = false; });
        if (tablist) tablist.hidden = true;
      });
      on(document, "shopify:block:select", e => {
        if (!root.contains(e.target)) return;
        const block = e.detail?.blockId;
        const tab = tabs.find(t => t.dataset.krBlockId === block || t.id.includes(block));
        if (tab) select(tab);
      });
    }
    if (root.dataset.krSection === "marquee") {
      const track = one("[data-kr-marquee-track]") || one("[data-kr-marquee]") || one(".kr-marquee-track");
      const group = one("[data-kr-marquee-group]") || one(".kr-marquee-group");
      const toggle = one("[data-kr-marquee-toggle]");
      if (group && track && !track.querySelector("[data-kr-clone]")) {
        const clone = group.cloneNode(true);
        clone.setAttribute("aria-hidden", "true"); clone.setAttribute("data-kr-clone", "");
        clone.removeAttribute("data-kr-marquee-group");
        [clone, ...clone.querySelectorAll("*")].forEach(el => {
          [...el.attributes].forEach(attr => {
            if (attr.name.startsWith("data-shopify-editor") || attr.name === "data-kr-block-id") el.removeAttribute(attr.name);
          });
        });
        track.append(clone); cleanups.push(() => clone.remove());
      }
      let paused = motion.matches;
      const setPaused = value => {
        paused = value; root.classList.toggle("kr-paused", value);
        toggle?.setAttribute("aria-pressed", String(value));
        if (toggle) {
          toggle.setAttribute("aria-label", value ? toggle.dataset.krResumeLabel : toggle.dataset.krPauseLabel);
          toggle.querySelector("span").textContent = value ? "▷" : "Ⅱ";
        }
      };
      setPaused(paused);
      if (toggle) toggle.hidden = false;
      on(toggle, "click", () => setPaused(!paused));
      on(motion, "change", () => setPaused(motion.matches));
    }
    if ("IntersectionObserver" in window && !motion.matches) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) { entry.target.classList.add("kr-is-revealing"); observer.unobserve(entry.target); }
        });
      }, { threshold: 0.12 });
      all(".kr-reveal").forEach(el => observer.observe(el));
      cleanups.push(() => observer.disconnect());
    }
    const botanical = one(".kr-botanical-image");
    if (botanical) {
      let frame = 0;
      const draw = () => {
        frame = 0;
        if (motion.matches) { botanical.style.removeProperty("--kr-parallax"); return; }
        const rect = botanical.getBoundingClientRect();
        if (rect.top < innerHeight && rect.bottom > 0) {
          const progress = (rect.top + rect.height / 2 - innerHeight / 2) / innerHeight;
          botanical.style.setProperty("--kr-parallax", `${Math.max(-12, Math.min(12, progress * 17))}px`);
        }
      };
      on(window, "scroll", () => { if (!frame) frame = requestAnimationFrame(draw); }, { passive: true });
      on(motion, "change", draw);
      cleanups.push(() => cancelAnimationFrame(frame));
    }
    if (root.dataset.krSection === "faq") {
      on(document, "shopify:block:select", e => {
        if (root.contains(e.target)) e.target.closest("details")?.setAttribute("open", "");
      });
    }
    instances.set(root, () => { controller.abort(); cleanups.forEach(fn => fn?.()); root.classList.remove("kr-enhanced"); });
  };
  const init = container => {
    if (container.matches?.("[data-kr-section]")) setup(container);
    container.querySelectorAll("[data-kr-section]").forEach(setup);
  };
  const unload = container => {
    for (const [root, cleanup] of instances) {
      if (root === container || container.contains(root)) { cleanup(); instances.delete(root); }
    }
  };
  window.kindriHome = { init, unload };
  document.addEventListener("shopify:section:load", event => init(event.target));
  document.addEventListener("shopify:section:unload", event => unload(event.target));
  init(document);
})();
